import * as Sentry from "@sentry/vue";
import type { App } from "vue";
import { inject, reactive, readonly } from "vue";
import type { RouteLocationNormalized } from "vue-router";

import { postApiAuthRefresh } from "@/api/account/api";

type AuthState = {
	accessToken: string | null;
	isAdmin: boolean;
	isAuthenticated: boolean;
	isLoading: boolean;
	refreshToken: string | null;
};

const AUTH_KEY = Symbol("auth");
const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

export const authState = reactive<AuthState>({
	accessToken: null,
	isAdmin: false,
	isAuthenticated: false,
	isLoading: true,
	refreshToken: null,
});

function decodeJwt<T extends Record<string, any>>(token: string): T | null {
	try {
		return JSON.parse(atob(token.split(".")[1])); // decode JWT
	} catch (e) {
		console.error("Invalid JWT token", e);

		return null;
	}
}

async function validateToken(token: string): Promise<boolean> {
	// Call an API to validate the token, maybe refresh the token?
	const payload = decodeJwt<{ exp: number }>(token);

	if (!payload) return false;

	return payload.exp * 1000 > Date.now();
}

/**
 * Determines if the token has admin privileges
 *
 * @param token
 * @returns
 */
function isTokenAdmin(token: string): boolean {
	const payload = decodeJwt<{ role?: string }>(token);

	if (!payload) return false;

	return !!payload.role && payload.role === "admin";
}

function setSentryUser(token: string | null) {
	if (!token) {
		Sentry.setUser(null);
		return;
	}

	const payload = decodeJwt<{ upn?: string; email?: string }>(token);

	Sentry.setUser({
		id: payload?.upn ?? payload?.email ?? "unknown",
		email: payload?.email ?? undefined,
	});
}

/**
 * Validates an accessToken & setup authentication state
 */
async function authenticate({
	accessToken,
	refreshToken,
}: {
	accessToken: string | null;
	refreshToken: string | null;
}) {
	if (accessToken && (await validateToken(accessToken))) {
		localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
		if (refreshToken) {
			localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
		}
		authState.accessToken = accessToken;
		authState.refreshToken = refreshToken;
		authState.isAdmin = isTokenAdmin(accessToken);
		authState.isAuthenticated = true;

		setSentryUser(authState.accessToken);

		return true;
	} else {
		clearSession();

		return false;
	}
}

function clearSession() {
	localStorage.removeItem(ACCESS_TOKEN_KEY);
	localStorage.removeItem(REFRESH_TOKEN_KEY);
	authState.accessToken = null;
	authState.isAdmin = false;
	authState.isAuthenticated = false;
	authState.refreshToken = null;

	setSentryUser(null);
}

/**
 * Validates an accessToken & setup authentication state
 */
export async function refreshSession() {
	try {
		if (authState.refreshToken) {
			const response = await postApiAuthRefresh({
				refreshToken: authState.refreshToken,
			});

			if (response.status === 200 && response.data && "accessToken" in response.data) {
				await authenticate(
					response.data as unknown as {
						accessToken: string;
						refreshToken: string;
					},
				);

				return true;
			}

			throw response;
		}

		throw Error("No Refresh Token");
	} catch (_e) {
		clearSession();

		return false;
	}
}

/**
 * Signs the current user out & clear the state
 */
async function logout() {
	clearSession();
}

export const authPlugin = async () => {
	const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

	await authenticate({ accessToken, refreshToken });

	authState.isLoading = false;

	return {
		install: async (app: App) => {
			app.provide(AUTH_KEY, readonly(authState));
		},
	};
};

export const authGuard = async (to: RouteLocationNormalized) => {
	const auth = authState;
	const requiresAuth = to.meta.requiresAuth ?? false;
	const requiresGuest = to.meta.requiresGuest ?? false;
	// const requiresOnboarding = to.meta.requiresOnboarding ?? false;
	const requiredRoles = (to.meta.requiredRoles as Array<string>) ?? [];

	if (requiresAuth) {
		if (!auth.isAuthenticated) {
			return {
				name: "AuthLogin",
				query: { redirectTo: to.fullPath },
			};
		}

		return true;
	}

	if (requiresGuest && auth.isAuthenticated) {
		return {
			name: "Dashboard",
		};
	}

	return true;
};

export function useAuth() {
	const auth = inject<AuthState>(AUTH_KEY);

	if (!auth) {
		console.error("useAuth must be used after installing auth plugin.");
		throw new Error("useAuth must be used after installing auth plugin.");
	}

	return {
		...auth,
		authenticate,
		logout,
	};
}
